<script>
import { getContrastingTextColor } from '@chatwoot/utils';

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    bgColor: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
  },
  computed: {
    // O chip corta o nome com reticencias, entao o tooltip nativo e o unico
    // jeito de ler o nome inteiro -- ele mostrava so a descricao, que na maioria
    // das etiquetas e vazia, e o nome truncado nao tinha como ser lido.
    fullLabel() {
      if (!this.title) return this.description;
      if (!this.description) return this.title;

      return `${this.title} — ${this.description}`;
    },
    textColor() {
      if (this.variant === 'smooth') return '';
      if (this.variant === 'dashed') return '';
      return this.color || getContrastingTextColor(this.bgColor);
    },
    labelClass() {
      return `label ${this.colorScheme} ${this.variant} ${
        this.small ? 'small' : ''
      }`;
    },
    labelStyle() {
      if (this.bgColor) {
        return {
          background: this.bgColor,
          color: this.textColor,
          border: `1px solid ${this.bgColor}`,
        };
      }
      return {};
    },
    anchorStyle() {
      if (this.bgColor) {
        return { color: this.textColor };
      }
      return {};
    },
  },
  methods: {
    onClick() {
      this.$emit('click', this.title);
    },
  },
};
</script>

<template>
  <div
    class="inline-flex ltr:mr-1 rtl:ml-1 mb-1"
    :class="labelClass"
    :style="labelStyle"
    :title="fullLabel"
  >
    <span v-if="icon" class="label-action--button">
      <fluent-icon :icon="icon" size="12" class="label--icon cursor-pointer" />
    </span>
    <span
      v-if="['smooth', 'dashed'].includes(variant) && title && !icon"
      :style="{ background: color }"
      class="label-color-dot flex-shrink-0"
    />
    <!-- `min-w-0` e o que faz a reticencia funcionar: como item de flex, sem
         ele o span nunca encolhe abaixo do conteudo, entao o
         `text-ellipsis` ja existente nunca chegava a agir e o chip so
         crescia. -->
    <span
      v-if="!href"
      class="min-w-0 whitespace-nowrap text-ellipsis overflow-hidden"
    >
      {{ title }}
    </span>
    <a v-else :href="href" :style="anchorStyle">{{ title }}</a>
    <button
      v-if="showClose"
      class="label-close--button p-0"
      :style="{ color: textColor }"
      @click="onClick"
    >
      <fluent-icon icon="dismiss" size="12" class="close--icon" />
    </button>
  </div>
</template>

<style scoped lang="scss">
// Chip de etiqueta. Mudancas frente ao visual herdado:
//
//   - pill (`rounded-full`) no lugar de `rounded-[4px]`, que era valor
//     arbitrario e dava um canto duro de interface antiga.
//   - padding horizontal separado do vertical. Era `p-1` uniforme, o que
//     deixava o texto encostado nas laterais.
//   - todos os esquemas passam a usar o mesmo par (50 de fundo, 700 de texto,
//     e 900/40 + 300 no escuro). Antes cada um pegava um degrau diferente e o
//     peso visual variava de chip para chip na mesma linha.
//   - o ponto de cor era `rounded-sm`, ou seja, um quadradinho. Virou circulo.
//
// As margens `mr-1 mb-1` do template continuam: etiquetas aparecem em linhas
// que quebram, e sem elas os chips colam. Quem devia ser dono desse
// espacamento e o container (gap), nao o chip -- fica para a fase de varredura.
.label {
  @apply items-center font-medium text-xs rounded-full gap-1.5 h-6 px-2.5
    bg-surface-sunken dark:bg-surface-sunken text-body dark:text-body;

  &.small {
    @apply text-xxs h-5 px-2 gap-1;
  }

  &.small .label--icon,
  &.small .close--icon {
    @apply text-[0.5rem];
  }

  a {
    @apply text-xs;
    &:hover {
      @apply underline;
    }
  }

  /* Esquemas de cor. Todos no mesmo par para terem peso visual igual. */
  &.primary {
    @apply bg-brand-subtle dark:bg-brand-subtle text-brand-text dark:text-brand-text;

    a {
      @apply text-brand-text dark:text-brand-text;
    }
    .label-color-dot {
      @apply bg-brand dark:bg-brand;
    }
  }
  &.secondary {
    @apply bg-surface-sunken dark:bg-surface-sunken text-muted dark:text-muted;

    a {
      @apply text-muted dark:text-muted;
    }
    .label-color-dot {
      @apply bg-subtle dark:bg-subtle;
    }
  }
  &.success {
    @apply bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300;

    a {
      @apply text-green-700 dark:text-green-300;
    }
    .label-color-dot {
      @apply bg-green-600 dark:bg-green-500;
    }
  }
  &.alert {
    @apply bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300;

    a {
      @apply text-red-700 dark:text-red-300;
    }
    .label-color-dot {
      @apply bg-red-600 dark:bg-red-500;
    }
  }
  &.warning {
    @apply bg-amber-50 text-amber-800;

    a {
      @apply text-amber-800;
    }
    // Era bg-yellow-900, quase preto, enquanto os outros pontos usavam -600.
    .label-color-dot {
      @apply bg-amber-600;
    }
  }

  // `smooth` e `dashed` sao os unicos com borda: e ela que os distingue, ja
  // que o fundo e transparente.
  &.smooth {
    @apply bg-transparent text-body dark:text-body border border-solid
      border-line dark:border-line;
  }

  &.dashed {
    @apply bg-transparent text-body dark:text-body border border-dashed
      border-line-strong dark:border-line-strong;
  }
}

.label-close--button {
  @apply text-current opacity-60 hover:opacity-100 rounded-full cursor-pointer
    flex items-center justify-center transition-opacity;
}

.label-action--button {
  @apply flex;
}

.label-color-dot {
  @apply inline-block w-2 h-2 rounded-full;
}
.label.small .label-color-dot {
  @apply w-1.5 h-1.5;
}
</style>
